import { db, storage } from "../firebase";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { normalizeActivityContributions } from "./activityContributions";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  limit,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  writeBatch,
  arrayUnion,
} from "firebase/firestore";
import { uniqueIds } from "./volunteerAssignments";

function normalizePhone(phone) {
  return `${phone || ""}`.replace(/\D/g, "");
}

function normalizeDevelopmentAttachment(attachment) {
  if (!attachment || typeof attachment !== "object" || !attachment.url || !attachment.path) {
    return null;
  }
  return {
    name: (attachment.name || "").trim(),
    url: attachment.url,
    path: attachment.path,
    type: (attachment.type || "").trim(),
    size: Number(attachment.size || 0),
  };
}

function buildDevelopmentAttachmentPath(folder, fileName = "image") {
  const safeName = `${fileName}`.replace(/[^a-zA-Z0-9._-]/g, "_") || "image";
  return `developmentNotes/${folder}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safeName}`;
}

async function uploadDevelopmentAttachment(file, folder) {
  if (!file) return null;
  const path = buildDevelopmentAttachmentPath(folder, file.name);
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, file, {
    contentType: file.type || "application/octet-stream",
    cacheControl: "public,max-age=3600",
  });
  const url = await getDownloadURL(snapshot.ref);
  return normalizeDevelopmentAttachment({
    name: file.name || "image",
    url,
    path,
    type: file.type || "",
    size: file.size || 0,
  });
}

async function deleteStorageFile(path) {
  if (!path) return;
  try {
    await deleteObject(ref(storage, path));
  } catch (error) {
    if (error?.code !== "storage/object-not-found") {
      throw error;
    }
  }
}

async function assertUniquePhone(collectionName, phoneRaw, phoneNormalized, currentId = "") {
  if (!phoneNormalized) return;

  const byNormalizedSnap = await getDocs(
    query(collection(db, collectionName), where("phoneNormalized", "==", phoneNormalized), limit(5))
  );
  const hasOtherByNormalized = byNormalizedSnap.docs.some((d) => d.id !== currentId);
  if (hasOtherByNormalized) {
    throw new Error("מספר הטלפון כבר קיים במערכת.");
  }

  if (!phoneRaw) return;
  const byRawSnap = await getDocs(
    query(collection(db, collectionName), where("phone", "==", phoneRaw), limit(5))
  );
  const hasOtherByRaw = byRawSnap.docs.some((d) => d.id !== currentId);
  if (hasOtherByRaw) {
    throw new Error("מספר הטלפון כבר קיים במערכת.");
  }
}

// ============ VOLUNTEERS ============
export async function getVolunteers() {
  const snap = await getDocs(query(collection(db, "volunteers"), orderBy("name")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getVolunteer(id) {
  const snap = await getDoc(doc(db, "volunteers", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function addVolunteer(data) {
  const firstName = (data.firstName || "").trim();
  const lastName = (data.lastName || "").trim();
  const fullName = (data.name || `${firstName} ${lastName}`.trim()).trim();
  const phone = (data.phone || "").trim();
  const phoneNormalized = normalizePhone(phone);
  const engagementType = data.engagementType === "casual" ? "casual" : "fixed";
  const isTeamLead = data.isTeamLead === true;
  const coordinatorIds = isTeamLead
    ? uniqueIds(data.coordinatorIds || data.coordinatorId || [])
    : uniqueIds(data.coordinatorIds || data.coordinatorId || []);
  const teamLeadVolunteerIds = uniqueIds(data.teamLeadVolunteerIds || data.teamLeadVolunteerId || []);
  const teamLeadVolunteerId = teamLeadVolunteerIds[0] || "";
  const coordinatorId = coordinatorIds[0] || "";
  await assertUniquePhone("volunteers", phone, phoneNormalized);
  const docRef = await addDoc(collection(db, "volunteers"), {
    ...data,
    firstName,
    lastName,
    name: fullName,
    phone,
    phoneNormalized,
    coordinatorId,
    coordinatorIds,
    engagementType,
    isTeamLead,
    teamLeadVolunteerId,
    teamLeadVolunteerIds,
    city: data.city || "",
    hebrewBirthday: data.hebrewBirthday || "",
    points: Number(data.points || 0),
    joinDate: data.joinDate || new Date().toISOString().split("T")[0],
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateVolunteer(id, data) {
  const firstName = (data.firstName || "").trim();
  const lastName = (data.lastName || "").trim();
  const fullName = (data.name || `${firstName} ${lastName}`.trim()).trim();
  const phone = (data.phone || "").trim();
  const phoneNormalized = normalizePhone(phone);
  const hasEngagementType = Object.prototype.hasOwnProperty.call(data, "engagementType");
  const engagementType = data.engagementType === "casual" ? "casual" : "fixed";
  const hasIsTeamLead = Object.prototype.hasOwnProperty.call(data, "isTeamLead");
  const hasTeamLeadVolunteerId = Object.prototype.hasOwnProperty.call(data, "teamLeadVolunteerId");
  const hasTeamLeadVolunteerIds = Object.prototype.hasOwnProperty.call(data, "teamLeadVolunteerIds");
  const hasCoordinatorId = Object.prototype.hasOwnProperty.call(data, "coordinatorId");
  const hasCoordinatorIds = Object.prototype.hasOwnProperty.call(data, "coordinatorIds");
  const normalizedIsTeamLead = data.isTeamLead === true;
  const normalizedCoordinatorIds = uniqueIds(data.coordinatorIds || data.coordinatorId || []);
  const normalizedCoordinatorId = normalizedCoordinatorIds[0] || "";
  const normalizedTeamLeadVolunteerIds = uniqueIds(data.teamLeadVolunteerIds || data.teamLeadVolunteerId || []);
  const normalizedTeamLeadVolunteerId = normalizedTeamLeadVolunteerIds[0] || "";
  await assertUniquePhone("volunteers", phone, phoneNormalized, id);
  await updateDoc(doc(db, "volunteers", id), {
    ...data,
    firstName,
    lastName,
    name: fullName,
    phone,
    phoneNormalized,
    ...(hasEngagementType ? { engagementType } : {}),
    ...(hasIsTeamLead ? { isTeamLead: normalizedIsTeamLead } : {}),
    ...(hasCoordinatorId || hasCoordinatorIds ? { coordinatorId: normalizedCoordinatorId, coordinatorIds: normalizedCoordinatorIds } : {}),
    ...(hasTeamLeadVolunteerId || hasTeamLeadVolunteerIds || hasIsTeamLead
      ? { teamLeadVolunteerId: normalizedTeamLeadVolunteerId, teamLeadVolunteerIds: normalizedTeamLeadVolunteerIds }
      : {}),
    points: Number(data.points || 0),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteVolunteer(id) {
  await deleteDoc(doc(db, "volunteers", id));
}

// ============ DOMAINS ============
export async function getDomains() {
  const snap = await getDocs(query(collection(db, "domains"), orderBy("name")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addDomain(data) {
  const docRef = await addDoc(collection(db, "domains"), {
    ...data,
    subdomains: Array.isArray(data.subdomains) ? data.subdomains : [],
    active: true,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateDomain(id, data) {
  await updateDoc(doc(db, "domains", id), data);
}

export async function deleteDomain(id) {
  await deleteDoc(doc(db, "domains", id));
}

// ============ CONTRIBUTION TYPES ============
export async function getContributionTypes() {
  const snap = await getDocs(query(collection(db, "contributionTypes"), orderBy("name")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addContributionType(data) {
  const docRef = await addDoc(collection(db, "contributionTypes"), {
    name: (data.name || "").trim(),
    unitLabel: (data.unitLabel || "").trim() || "פריטים",
    unitValue:
      data.unitValue === "" || data.unitValue === null || data.unitValue === undefined
        ? null
        : Number(data.unitValue),
    active: data.active !== false,
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateContributionType(id, data) {
  const patch = { ...data };
  if (Object.prototype.hasOwnProperty.call(patch, "name")) {
    patch.name = (patch.name || "").trim();
  }
  if (Object.prototype.hasOwnProperty.call(patch, "unitLabel")) {
    patch.unitLabel = (patch.unitLabel || "").trim() || "פריטים";
  }
  if (Object.prototype.hasOwnProperty.call(patch, "unitValue")) {
    patch.unitValue =
      patch.unitValue === "" || patch.unitValue === null || patch.unitValue === undefined
        ? null
        : Number(patch.unitValue);
  }
  await updateDoc(doc(db, "contributionTypes", id), patch);
}

export async function deleteContributionType(id) {
  await deleteDoc(doc(db, "contributionTypes", id));
}

// ============ ACTIVITIES ============
export async function getActivities(filters = {}) {
  let q = collection(db, "activities");
  const constraints = [orderBy("date", "desc")];

  if (filters.volunteerId) {
    constraints.unshift(where("volunteerId", "==", filters.volunteerId));
  }
  if (filters.domainId) {
    constraints.unshift(where("domainId", "==", filters.domainId));
  }

  const snap = await getDocs(query(q, ...constraints));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addActivity(data, createdBy) {
  const volunteerId = (data.volunteerId || "").trim();
  const hasVolunteer = !!volunteerId;
  const normalizedStatus = (data.status || "").trim();
  const contributions = normalizeActivityContributions(data.contributions);
  const docRef = await addDoc(collection(db, "activities"), {
    ...data,
    beneficiaryId: data.beneficiaryId || "",
    volunteerId,
    city: (data.city || "").trim(),
    internalCoordinatorId: (data.internalCoordinatorId || "").trim(),
    contributions,
    duration: Number(data.duration),
    subdomain: (data.subdomain || "").trim(),
    status: hasVolunteer ? "matched" : (normalizedStatus === "matched" ? "pending" : (normalizedStatus || "pending")),
    source: data.source || "manual",
    createdBy: createdBy || "",
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateActivity(id, data) {
  const patch = { ...data };
  if (Object.prototype.hasOwnProperty.call(patch, "city")) {
    patch.city = (patch.city || "").trim();
  }
  if (Object.prototype.hasOwnProperty.call(patch, "internalCoordinatorId")) {
    patch.internalCoordinatorId = (patch.internalCoordinatorId || "").trim();
  }
  if (Object.prototype.hasOwnProperty.call(patch, "contributions")) {
    patch.contributions = normalizeActivityContributions(patch.contributions);
  }
  if (Object.prototype.hasOwnProperty.call(patch, "volunteerId")) {
    const normalizedVolunteerId = (patch.volunteerId || "").trim();
    patch.volunteerId = normalizedVolunteerId;
    if (normalizedVolunteerId) {
      patch.status = "matched";
    } else {
      const normalizedStatus = (patch.status || "").trim();
      if (!normalizedStatus || normalizedStatus === "matched") {
        patch.status = "pending";
      }
    }
  }
  await updateDoc(doc(db, "activities", id), patch);
}

export async function deleteActivity(id) {
  await deleteDoc(doc(db, "activities", id));
}

// ============ RECURRING ACTIVITIES ============
export async function getRecurringActivities() {
  const snap = await getDocs(collection(db, "recurringActivities"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addRecurringActivity(data) {
  const normalizedStatus = (data.status || "").trim() || "active";
  const approvalStatus = (data.approvalStatus || "").trim() || "pending";
  const docRef = await addDoc(collection(db, "recurringActivities"), {
    ...data,
    beneficiaryId: (data.beneficiaryId || "").trim(),
    city: (data.city || "").trim(),
    contributions: normalizeActivityContributions(data.contributions),
    duration: Number(data.duration),
    day: Number(data.day),
    status: normalizedStatus,
    approvalStatus,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateRecurringActivity(id, data) {
  const patch = { ...data };
  if (Object.prototype.hasOwnProperty.call(patch, "beneficiaryId")) {
    patch.beneficiaryId = (patch.beneficiaryId || "").trim();
  }
  if (Object.prototype.hasOwnProperty.call(patch, "city")) {
    patch.city = (patch.city || "").trim();
  }
  if (Object.prototype.hasOwnProperty.call(patch, "contributions")) {
    patch.contributions = normalizeActivityContributions(patch.contributions);
  }
  if (Object.prototype.hasOwnProperty.call(patch, "approvalStatus")) {
    patch.approvalStatus = (patch.approvalStatus || "").trim() || "pending";
  }
  if (Object.prototype.hasOwnProperty.call(patch, "status")) {
    patch.status = (patch.status || "").trim() || "active";
  }
  patch.updatedAt = serverTimestamp();
  await updateDoc(doc(db, "recurringActivities", id), patch);
}

export async function deleteRecurringActivity(id) {
  await deleteDoc(doc(db, "recurringActivities", id));
}

function parseIsoDate(iso) {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function autoActivityDocId(recurringId, date) {
  return `auto_${recurringId}_${date}`;
}

function getRecurringKeyFromAutoActivity(row) {
  if (!row || row.source !== "auto") return "";
  if (row.recurringKey) return row.recurringKey;
  if (row.recurringId && row.date) return `${row.recurringId}_${row.date}`;
  return "";
}

export async function syncRecurringActivitiesUntilToday({ lookbackDays = 370 } = {}) {
  const today = new Date();
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const minDate = new Date(todayDate);
  minDate.setDate(minDate.getDate() - lookbackDays);

  const recurringSnap = await getDocs(
    query(collection(db, "recurringActivities"), where("status", "==", "active"))
  );
  if (recurringSnap.empty) return 0;

  const autoSnap = await getDocs(query(collection(db, "activities"), where("source", "==", "auto")));
  const autoDocs = autoSnap.docs.map((d) => ({ id: d.id, row: d.data() || {} }));
  const existingKeys = new Set(autoDocs.map(({ row }) => getRecurringKeyFromAutoActivity(row)).filter(Boolean));

  // Clean up historical duplicates (same recurringKey more than once), keep one doc per key.
  const byKey = {};
  autoDocs.forEach(({ id, row }) => {
    const key = getRecurringKeyFromAutoActivity(row);
    if (!key) return;
    if (!byKey[key]) byKey[key] = [];
    byKey[key].push({ id, row });
  });
  const duplicateIdsToDelete = [];
  Object.entries(byKey).forEach(([key, rows]) => {
    if (rows.length <= 1) return;
    const [recurringId, date] = key.split("_");
    const preferredId = recurringId && date ? autoActivityDocId(recurringId, date) : "";
    let keepId = rows[0].id;
    if (preferredId && rows.some((x) => x.id === preferredId)) {
      keepId = preferredId;
    }
    rows.forEach((x) => {
      if (x.id !== keepId) duplicateIdsToDelete.push(x.id);
    });
  });
  for (let i = 0; i < duplicateIdsToDelete.length; i += 400) {
    const chunk = duplicateIdsToDelete.slice(i, i + 400);
    const batch = writeBatch(db);
    chunk.forEach((id) => {
      batch.delete(doc(db, "activities", id));
    });
    await batch.commit();
  }

  const toCreate = [];
  recurringSnap.docs.forEach((docSnap) => {
    const r = { id: docSnap.id, ...docSnap.data() };
    if ((r.approvalStatus || "pending") !== "approved") return;
    const startByRule = parseIsoDate(r.startDate) || minDate;
    const endByRule = parseIsoDate(r.endDate) || todayDate;
    const start = startByRule > minDate ? startByRule : minDate;
    const end = endByRule < todayDate ? endByRule : todayDate;
    if (start > end) return;

    const cursor = new Date(start);
    while (cursor <= end) {
      if (cursor.getDay() === Number(r.day)) {
        const date = isoDate(cursor);
        const recurringKey = `${r.id}_${date}`;
        if (!existingKeys.has(recurringKey)) {
          const docId = autoActivityDocId(r.id, date);
          toCreate.push({
            docId,
            row: {
              recurringKey,
              recurringId: r.id,
              volunteerId: r.volunteerId || "",
              beneficiaryId: r.beneficiaryId || "",
              domainId: r.domainId || "",
              city: (r.city || "").trim(),
              date,
              time: r.time || "09:00",
              duration: Number(r.duration || 1),
              contributions: normalizeActivityContributions(r.contributions),
              notes: r.notes || "",
              source: "auto",
              createdBy: "system-recurring",
              createdAt: serverTimestamp(),
            },
          });
          existingKeys.add(recurringKey);
        }
      }
      cursor.setDate(cursor.getDate() + 1);
    }
  });

  if (toCreate.length === 0) return 0;

  let created = 0;
  for (let i = 0; i < toCreate.length; i += 400) {
    const chunk = toCreate.slice(i, i + 400);
    const batch = writeBatch(db);
    chunk.forEach(({ docId, row }) => {
      const ref = doc(db, "activities", docId);
      batch.set(ref, row, { merge: false });
    });
    await batch.commit();
    created += chunk.length;
  }

  return created;
}

// ============ BENEFICIARIES (NEEZARIM) ============
export async function getBeneficiaries() {
  const snap = await getDocs(query(collection(db, "beneficiaries"), orderBy("name")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getBeneficiariesForVolunteer(volunteerId) {
  if (!volunteerId) return [];
  const snap = await getDocs(
    query(collection(db, "beneficiaries"), where("volunteerIds", "array-contains", volunteerId))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addBeneficiary(data) {
  const phone = (data.phone || "").trim();
  const phoneNormalized = normalizePhone(phone);
  const secondaryPhone = (data.secondaryPhone || "").trim();
  const secondaryPhoneNormalized = normalizePhone(secondaryPhone);
  await assertUniquePhone("beneficiaries", phone, phoneNormalized);
  const docRef = await addDoc(collection(db, "beneficiaries"), {
    name: (data.name || "").trim(),
    address: (data.address || "").trim(),
    city: (data.city || "").trim(),
    phone,
    phoneNormalized,
    secondaryContactName: (data.secondaryContactName || "").trim(),
    secondaryPhone,
    secondaryPhoneNormalized,
    coordinatorId: (data.coordinatorId || "").trim(),
    intakeCoordinatorMode: data.intakeCoordinatorMode === "manual" ? "manual" : "auto",
    intakeCoordinatorId: (data.intakeCoordinatorId || "").trim(),
    intakeCoordinatorName: (data.intakeCoordinatorName || "").trim(),
    createdByUserId: (data.createdByUserId || "").trim(),
    createdByName: (data.createdByName || "").trim(),
    domain: data.domain || "other",
    volunteerIds: data.volunteerIds || [],
    active: data.active ?? true,
    notes: (data.notes || "").trim(),
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateBeneficiary(id, data) {
  const phone = (data.phone || "").trim();
  const phoneNormalized = normalizePhone(phone);
  const secondaryPhone = (data.secondaryPhone || "").trim();
  const secondaryPhoneNormalized = normalizePhone(secondaryPhone);
  await assertUniquePhone("beneficiaries", phone, phoneNormalized, id);
  const patch = {
    ...data,
    phone,
    phoneNormalized,
    secondaryContactName: (data.secondaryContactName || "").trim(),
    secondaryPhone,
    secondaryPhoneNormalized,
    updatedAt: serverTimestamp(),
  };
  if (Object.prototype.hasOwnProperty.call(data, "coordinatorId")) {
    patch.coordinatorId = (data.coordinatorId || "").trim();
  }
  if (Object.prototype.hasOwnProperty.call(data, "intakeCoordinatorMode")) {
    patch.intakeCoordinatorMode = data.intakeCoordinatorMode === "manual" ? "manual" : "auto";
  }
  if (Object.prototype.hasOwnProperty.call(data, "intakeCoordinatorId")) {
    patch.intakeCoordinatorId = (data.intakeCoordinatorId || "").trim();
  }
  if (Object.prototype.hasOwnProperty.call(data, "intakeCoordinatorName")) {
    patch.intakeCoordinatorName = (data.intakeCoordinatorName || "").trim();
  }
  if (Object.prototype.hasOwnProperty.call(data, "createdByUserId")) {
    patch.createdByUserId = (data.createdByUserId || "").trim();
  }
  if (Object.prototype.hasOwnProperty.call(data, "createdByName")) {
    patch.createdByName = (data.createdByName || "").trim();
  }
  await updateDoc(doc(db, "beneficiaries", id), patch);
}

export async function deleteBeneficiary(id) {
  await deleteDoc(doc(db, "beneficiaries", id));
}

// ============ DEVELOPMENT NOTES ============
export async function getDevelopmentNotes() {
  const snap = await getDocs(query(collection(db, "developmentNotes"), orderBy("createdAt", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addDevelopmentNote(data) {
  const attachment = data.attachmentFile
    ? await uploadDevelopmentAttachment(data.attachmentFile, "notes")
    : normalizeDevelopmentAttachment(data.attachment);
  const docRef = await addDoc(collection(db, "developmentNotes"), {
    title: (data.title || "").trim(),
    body: (data.body || "").trim(),
    authorId: (data.authorId || "").trim(),
    authorName: (data.authorName || "").trim(),
    authorRole: (data.authorRole || "").trim(),
    status: (data.status || "open").trim() || "open",
    attachment,
    replies: Array.isArray(data.replies) ? data.replies : [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateDevelopmentNote(id, data) {
  const patch = {
    ...data,
    ...(Object.prototype.hasOwnProperty.call(data, "title") ? { title: (data.title || "").trim() } : {}),
    ...(Object.prototype.hasOwnProperty.call(data, "body") ? { body: (data.body || "").trim() } : {}),
    ...(Object.prototype.hasOwnProperty.call(data, "status") ? { status: (data.status || "open").trim() || "open" } : {}),
    updatedAt: serverTimestamp(),
  };

  if (Object.prototype.hasOwnProperty.call(data, "attachmentFile")) {
    if (data.previousAttachment?.path) {
      await deleteStorageFile(data.previousAttachment.path);
    }
    patch.attachment = data.attachmentFile
      ? await uploadDevelopmentAttachment(data.attachmentFile, "notes")
      : null;
  } else if (Object.prototype.hasOwnProperty.call(data, "attachment")) {
    const nextAttachment = normalizeDevelopmentAttachment(data.attachment);
    if (!nextAttachment && data.previousAttachment?.path) {
      await deleteStorageFile(data.previousAttachment.path);
    }
    patch.attachment = nextAttachment;
  }

  await updateDoc(doc(db, "developmentNotes", id), {
    ...patch,
  });
}

export async function addDevelopmentNoteReply(id, data) {
  const attachment = data.attachmentFile
    ? await uploadDevelopmentAttachment(data.attachmentFile, "replies")
    : normalizeDevelopmentAttachment(data.attachment);
  const reply = {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    body: (data.body || "").trim(),
    authorId: (data.authorId || "").trim(),
    authorName: (data.authorName || "").trim(),
    authorRole: (data.authorRole || "").trim(),
    attachment,
    createdAt: new Date().toISOString(),
  };
  if (!reply.body) {
    throw new Error("יש למלא תגובה.");
  }
  await updateDoc(doc(db, "developmentNotes", id), {
    replies: arrayUnion(reply),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteDevelopmentNote(noteOrId) {
  const note = typeof noteOrId === "string" ? null : noteOrId;
  const noteId = typeof noteOrId === "string" ? noteOrId : noteOrId?.id;
  if (!noteId) return;

  if (note?.attachment?.path) {
    await deleteStorageFile(note.attachment.path);
  }
  if (Array.isArray(note?.replies)) {
    for (const reply of note.replies) {
      if (reply?.attachment?.path) {
        await deleteStorageFile(reply.attachment.path);
      }
    }
  }

  await deleteDoc(doc(db, "developmentNotes", noteId));
}

// ============ USERS ============
export async function getUsers() {
  const snap = await getDocs(collection(db, "users"));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "he"));
}

export async function updateUser(id, data) {
  await updateDoc(doc(db, "users", id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteUser(id) {
  await deleteDoc(doc(db, "users", id));
}

export async function addUser(data) {
  const email = (data.email || "").trim().toLowerCase();
  if (!email) {
    throw new Error("יש להזין כתובת אימייל.");
  }

  const existing = await getDoc(doc(db, "users", email));
  if (existing.exists()) {
    throw new Error("משתמש עם אימייל זה כבר קיים.");
  }

  const baseUser = {
    name: (data.name || "").trim(),
    email,
    role: data.role || "volunteer",
    parentCoordinatorId: data.role === "group_coordinator" ? (data.parentCoordinatorId || "").trim() : "",
    managedDomainIds: Array.isArray(data.managedDomainIds) ? data.managedDomainIds.filter(Boolean) : [],
    permissions: data.permissions && typeof data.permissions === "object" ? data.permissions : {},
    active: data.active ?? true,
    volunteerId: data.volunteerId || null,
    createdAt: serverTimestamp(),
  };

  let volunteerId = baseUser.volunteerId;
  if ((baseUser.role === "volunteer" || (baseUser.role === "group_coordinator" && data.createVolunteerProfile === true)) && !volunteerId) {
    volunteerId = await ensureVolunteerForEmail(baseUser.name, email);
  }

  await setDoc(doc(db, "users", email), {
    ...baseUser,
    volunteerId: volunteerId || null,
  });
  return email;
}

async function ensureVolunteerForEmail(name, email) {
  const byExact = await getDocs(query(collection(db, "volunteers"), where("email", "==", email), limit(2)));
  if (byExact.size === 1) return byExact.docs[0].id;

  if (byExact.empty) {
    const allVols = await getDocs(collection(db, "volunteers"));
    const normalized = allVols.docs.filter((d) => ((d.data().email || "").trim().toLowerCase() === email));
    if (normalized.length === 1) return normalized[0].id;
  }

  const docRef = await addDoc(collection(db, "volunteers"), {
    firstName: (name || "").trim(),
    lastName: "",
    name: name || email.split("@")[0],
    phone: "",
    phoneNormalized: "",
    engagementType: "fixed",
    city: "",
    email,
    hebrewBirthday: "",
    points: 0,
    domains: [],
    status: "active",
    joinDate: new Date().toISOString().split("T")[0],
    notes: "",
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function syncUserVolunteer(userId) {
  const userSnap = await getDoc(doc(db, "users", userId));
  if (!userSnap.exists()) return;
  const user = userSnap.data();
  if (user.role !== "volunteer" && user.role !== "group_coordinator") return;

  let volunteerId = user.volunteerId || null;
  if (!volunteerId) {
    volunteerId = await ensureVolunteerForEmail((user.name || "").trim(), (user.email || "").trim().toLowerCase());
  }

  await updateDoc(doc(db, "users", userId), {
    volunteerId,
    updatedAt: serverTimestamp(),
  });
}

// ============ SEED DATA ============
export async function seedInitialData() {
  // Check if domains exist
  const domainsSnap = await getDocs(collection(db, "domains"));
  if (domainsSnap.size > 0) return false; // Already seeded

  const batch = writeBatch(db);

  const domainsData = [
    { name: "ליווי חולים", color: "#C41E3A", active: true },
    { name: "הסעות", color: "#2563EB", active: true },
    { name: "סיוע טכנולוגי", color: "#16A34A", active: true },
    { name: "חלוקת מזון", color: "#EA580C", active: true },
    { name: "תמיכה נפשית", color: "#7C3AED", active: true },
  ];

  domainsData.forEach((d) => {
    const ref = doc(collection(db, "domains"));
    batch.set(ref, { ...d, createdAt: Timestamp.now() });
  });

  await batch.commit();
  return true;
}
